import { useRef, useState, type FormEvent } from 'react';
import { createRoot } from 'react-dom/client';
import * as RadixDialog from '@radix-ui/react-dialog';

interface Common {
  title: string;
  body?: string;
  confirmLabel?: string;
  /** Marks a change that is hard to undo, so the confirming button is styled as a warning. */
  danger?: boolean;
}

/**
 * A proper dialog in place of the browser's confirm and prompt boxes: named,
 * modal with a real focus trap (Radix), closes on Escape, and returns focus to
 * whatever opened it. `text` set means it also asks for a line of text.
 */
function Dialog(props: {
  common: Common;
  text?: { label: string; value: string; required: boolean };
  done(answer: string | null): void;
}) {
  const { common, text } = props;
  const [value, setValue] = useState(text?.value ?? '');
  const cancel = useRef<HTMLButtonElement>(null);
  const field = useRef<HTMLInputElement>(null);

  function submit(e: FormEvent) {
    e.preventDefault();
    if (text?.required && value.trim() === '') return;
    props.done(text ? value.trim() : 'yes');
  }

  return (
    <RadixDialog.Root open onOpenChange={(open) => !open && props.done(null)}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="backdrop">
          <RadixDialog.Content
            className="dialog"
            aria-describedby={common.body ? 'ask-b' : undefined}
            // Ask for the text if there is one; otherwise land on Cancel so Enter never destroys anything.
            onOpenAutoFocus={(e) => {
              e.preventDefault();
              (field.current ?? cancel.current)?.focus();
            }}
            // Only Cancel, the confirm button or Escape close it, as before: a stray click never dismisses.
            onInteractOutside={(e) => e.preventDefault()}
            // We restore focus to the opener ourselves in `open`.
            onCloseAutoFocus={(e) => e.preventDefault()}
          >
            <form className="stack" onSubmit={submit} noValidate>
              <RadixDialog.Title asChild>
                <h2>{common.title}</h2>
              </RadixDialog.Title>
              {common.body && (
                <RadixDialog.Description id="ask-b" className="muted">
                  {common.body}
                </RadixDialog.Description>
              )}
              {text && (
                <label>
                  {text.label}
                  <input ref={field} value={value} onChange={(e) => setValue(e.target.value)} />
                </label>
              )}
              <div className="row end">
                <button
                  ref={cancel}
                  type="button"
                  className="quiet"
                  onClick={() => props.done(null)}
                >
                  Cancel
                </button>
                <button type="submit" className={common.danger ? 'danger-fill' : undefined}>
                  {common.confirmLabel ?? 'OK'}
                </button>
              </div>
            </form>
          </RadixDialog.Content>
        </RadixDialog.Overlay>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

function open(
  common: Common,
  text?: { label: string; value: string; required: boolean },
): Promise<string | null> {
  const opener = document.activeElement as HTMLElement | null;
  const host = document.createElement('div');
  document.body.appendChild(host);
  const root = createRoot(host);
  return new Promise((resolve) => {
    root.render(
      <Dialog
        common={common}
        text={text}
        done={(answer) => {
          root.unmount();
          host.remove();
          opener?.focus?.();
          resolve(answer);
        }}
      />,
    );
  });
}

/** Ask a yes or no question. Resolves true only if they confirm. */
export const askConfirm = async (common: Common): Promise<boolean> => (await open(common)) !== null;

/** Ask for a line of text. Resolves to the text, or null if they cancel. */
export const askText = (
  common: Common & { label: string; value?: string; required?: boolean },
): Promise<string | null> =>
  open(common, {
    label: common.label,
    value: common.value ?? '',
    required: common.required ?? false,
  });
